/**
 * `/breadth/buckets` — the Buckets Brief payload (`services/qv-data/app/qf/buckets_brief.py`).
 *
 * Reads, so a failure throws and the panel says so; there is nothing to retry
 * that the next 60-second refresh will not.
 */
import { authHeaders } from "../lib/session";
import { httpBase } from "./backend";

const URL_BASE = httpBase(
  import.meta.env.VITE_QV_IND_URL as string | undefined,
  import.meta.env.VITE_QV_SET_URL as string | undefined,
);

export interface ListBuckets {
  /** The session shown as today, `YYYY-MM-DD`. */
  date: string;
  /** The session before it. */
  prevDate: string;
  /** Percent of the list in each bucket 0–11. */
  today: number[];
  yesterday: number[];
  /** The list's stocks with data — the denominator. */
  stocks: number;
}

/** A list's PIP: its index's daily bars, `[date, open, high, low, close]`. */
export interface PipData {
  symbol: string;
  bars: Array<[string, number, number, number, number]>;
}

export interface BucketsPayload {
  lists: Record<string, ListBuckets | null>;
  pips?: Record<string, PipData | null>;
  /** True while today's bars move with the 1-minute snapshot. */
  live: boolean;
}

export async function fetchBuckets(lists: readonly string[], date?: string): Promise<BucketsPayload> {
  const params = new URLSearchParams({ lists: lists.join(",") });
  if (date) params.set("date", date);
  const res = await fetch(`${URL_BASE.replace(/\/$/, "")}/breadth/buckets?${params}`, {
    headers: await authHeaders(),
  });
  if (!res.ok) {
    const detail = await res.json().then((b) => b?.detail).catch(() => undefined);
    throw new Error(detail ?? `Buckets Brief: ${res.status}`);
  }
  return (await res.json()) as BucketsPayload;
}

/** One session of a replay. */
export interface BucketFrame {
  date: string;
  pct: number[];
  stocks: number;
}

export interface BucketSeries {
  /** Per list, oldest first: the sessions of the replay, plus the one before the first. */
  lists: Record<string, BucketFrame[] | null>;
  pips?: Record<string, PipData | null>;
  live: boolean;
}

/** Every frame of a replay of `days` sessions ending at `date` (or today), in one request. */
export async function fetchBucketSeries(
  lists: readonly string[],
  days: number,
  date?: string,
): Promise<BucketSeries> {
  const params = new URLSearchParams({ lists: lists.join(","), days: String(days) });
  if (date) params.set("date", date);
  const res = await fetch(`${URL_BASE.replace(/\/$/, "")}/breadth/buckets/series?${params}`, {
    headers: await authHeaders(),
  });
  if (!res.ok) {
    const detail = await res.json().then((b) => b?.detail).catch(() => undefined);
    throw new Error(detail ?? `Buckets Brief replay: ${res.status}`);
  }
  return (await res.json()) as BucketSeries;
}
